<template>
  <div class="app-container">
    <div class="search-container">
      <div class="search-header">
        <h1>AI智能搜题</h1>
        <p>输入题目内容或上传图片，快速找到相似题目和解析</p>
      </div>

      <el-card class="search-box-card">
        <div class="search-box">
          <el-input v-model="searchQuery" type="textarea" :rows="4" placeholder="请输入题目内容或粘贴图片(Ctrl+V)..." class="search-input" @paste="handlePaste" ></el-input>
          <div class="upload-preview" v-if="imageUrl">
            <div class="preview-wrapper">
              <img :src="imageUrl" alt="预览图片" class="preview-image">
              <div class="preview-actions">
                <el-button type="danger" size="mini" icon="el-icon-delete" circle @click="removeImage"></el-button>
              </div>
            </div>
          </div>

          <div class="search-actions">
            <el-upload
              class="image-upload"
              action="#"
              :show-file-list="false"
              :before-upload="beforeUpload"
              accept="image/*"
            >
              <el-button type="primary" plain icon="el-icon-picture-outline">上传图片</el-button>
            </el-upload>
            <el-button type="primary" @click="searchQuestions" :loading="loading" icon="el-icon-search">搜索题目</el-button>
          </div>
        </div>
      </el-card>

      <div v-if="aiResponse" class="search-results">
        <h2>AI 解析结果</h2>
        <el-card class="result-card">
          <div class="ai-response">
            <div class="ai-avatar">
              <i class="el-icon-chat-dot-round"></i>
            </div>
            <div class="ai-content" v-html="formattedResponse"></div>
          </div>
        </el-card>
      </div>

      <div v-if="noResults && !loading" class="no-results">
        <el-card class="empty-card">
          <div class="empty-container">
            <i class="el-icon-warning-outline empty-icon"></i>
            <span class="empty-text">未找到相关题目</span>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { textCompletion, uploadAndAnalyzeImage } from '@/api/ai'
import { marked } from 'marked' // 修改为 { marked } 导入方式
import { Empty } from 'element-ui'

export default {
  name: 'AISearch',
  components: {
  },
  data() {
    return {
      searchQuery: '',
      aiResponse: '',
      loading: false,
      noResults: false,
      imageUrl: '', // 图片URL
      imageFile: null, // 图片文件对象
      searchCount: 0 // 添加搜索次数计数器
    }
  },
  computed: {
    formattedResponse() {
      if (!this.aiResponse) return ''
      // 使用 marked 将 markdown 格式转换为 HTML
      try {
        // 增加katex渲染支持
        const renderer = new marked.Renderer()
        const originalCode = renderer.code

        renderer.code = function(code, language) {
          if (language === 'latex' || language === 'math' || language === 'tex') {
            return `<div class="math-formula">${code}</div>`
          }
          return originalCode.call(this, code, language)
        }

        // 配置marked选项
        marked.setOptions({
          renderer: renderer,
          breaks: true,
          gfm: true
        })

        // 预处理Markdown内容，特别处理数学公式部分
        let content = this.aiResponse

        // 1. 检测并标准化数学公式标记
        // 将$符号的公式转换为\( \)格式
        content = content
          .replace(/(?<!\$|\\\()(?<!\\\))\$(?!\$)([^$\n]+?)(?<!\$)\$(?!\$)(?!\\\))/g, '\\($1\\)')
          .replace(/\$\$([^$]+?)\$\$/g, '\\[$1\\]')

        // 2. 处理LaTeX公式的HTML包装
        content = content
          // 行内公式
          .replace(/\\\(/g, '<span class="inline-formula">\\(')
          .replace(/\\\)/g, '\\)</span>')
          // 行间公式
          .replace(/\\\[/g, '<div class="block-formula">\\[')
          .replace(/\\\]/g, '\\]</div>')

        // 应用Markdown转换
        const htmlContent = marked(content)

        // 在下一个tick中执行公式渲染
        this.$nextTick(() => {
          this.renderMathFormulas()
        })

        return htmlContent
      } catch (e) {
        console.error('Markdown转换失败:', e)
        return this.aiResponse
      }
    }
  },
  mounted() {
    // 添加全局粘贴事件监听
    document.addEventListener('paste', this.handlePaste)
  },

  beforeDestroy() {
    // 移除全局粘贴事件监听
    document.removeEventListener('paste', this.handlePaste)
  },

  methods: {
    // 处理粘贴事件，用于粘贴图片
    handlePaste(event) {
      console.log('粘贴事件触发', event)
      const items = (event.clipboardData || window.clipboardData).items

      if (!items) {
        console.log('无法获取剪贴板数据')
        return
      }

      console.log('剪贴板项目数量:', items.length)

      let foundImage = false
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        console.log('剪贴板项目类型:', item.type)

        if (item.type.indexOf('image') !== -1) {
          // 阻止默认粘贴行为
          event.preventDefault()
          foundImage = true

          // 从剪贴板获取图片文件
          const blob = item.getAsFile()
          if (!blob) {
            console.error('无法获取图片文件')
            continue
          }
          this.imageFile = blob
          // 创建预览URL
          this.imageUrl = URL.createObjectURL(blob)
          break
        }
      }

      if (!foundImage) {
        console.log('剪贴板中未找到图片')
      }
    },

    // 上传图片前的处理
    beforeUpload(file) {
      // 验证图片类型
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }

      // 验证图片大小 (限制为10MB)
      const isLessThan10MB = file.size / 1024 / 1024 < 10
      if (!isLessThan10MB) {
        this.$message.error('图片大小不能超过10MB!')
        return false
      }

      // 保存文件
      this.imageFile = file
      this.imageUrl = URL.createObjectURL(file)

      // 阻止自动上传
      return false
    },

    // 移除图片
    removeImage() {
      if (this.imageUrl && this.imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.imageUrl)
      }
      this.imageUrl = ''
      this.imageFile = null
    },

    // 将图片转换为Base64
    getBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },

    searchQuestions() {
      if (!this.searchQuery.trim() && !this.imageFile) {
        this.$message.warning('请输入搜索内容或上传图片')
        return
      }

      this.loading = true
      this.noResults = false
      this.aiResponse = ''

      // 增加搜索次数
      this.searchCount++

      // 根据是否有图片决定调用哪个API
      if (this.imageFile) {
        this.searchWithImage()
      } else {
        this.searchWithText()
      }
    },

    // 使用纯文本搜索
    searchWithText() {
      // 根据搜索次数构建不同的提示词
      let prompt = ''

      if (this.searchCount === 1) {
        // 第一次搜索，使用原有的提示词
        prompt = `请帮我分析以下题目，但不要直接给出答案，只提供三个解题思路或方向提示：${this.searchQuery}

        请按照以下格式回复：
        ## 题目分析
        [简要分析题目要求和关键信息]

        ## 解题思路
        1. [第一个思路提示]
        2. [第二个思路提示]
        3. [第三个思路提示]

        注意：请不要直接给出答案，只提供思路引导。`
      } else {
        // 第二次及以后的搜索，提供更详细的提示
        prompt = `请帮我详细分析以下题目，提供更加深入的解题思路和方法，但仍然不要直接给出完整答案：${this.searchQuery}

        请按照以下格式回复：
        ## 题目分析
        [详细分析题目要求、关键信息和解题方向]

        ## 详细解题思路
        1. [第一个思路的详细说明，包括相关知识点和应用方法]
        2. [第二个思路的详细说明，包括相关知识点和应用方法]
        3. [第三个思路的详细说明，包括相关知识点和应用方法]

        ## 关键步骤提示
        [提供解题的关键步骤和方法，但不给出完整解答]

        ## 相关知识点
        [列出与题目相关的知识点和概念，帮助理解]

        注意：请不要直接给出完整答案，而是提供更详细的解题指导和思路分析。`
      }

      // 调用后端 AI 文本接口
      textCompletion(prompt)
        .then(this.handleApiResponse)
        .catch(this.handleApiError)
        .finally(() => {
          this.loading = false
        })
    },

    // 使用图片搜索
    async searchWithImage() {
      try {
        // 检查图片大小
        if (this.imageFile.size > 10 * 1024 * 1024) {
          this.$message.warning('图片过大，可能导致上传失败。正在尝试压缩...')
        }

        // 构建提示词
        let prompt = ''

        if (this.searchCount === 1) {
          // 第一次搜索，使用原有的提示词
          prompt = '请分析图片中的题目，但不要直接给出答案，只提供三个解题思路或方向提示。请按照以下格式回复：\n## 题目分析\n[简要分析题目要求和关键信息]\n\n## 解题思路\n1. [第一个思路提示]\n2. [第二个思路提示]\n3. [第三个思路提示]\n\n注意：请不要直接给出答案，只提供思路引导。'
        } else {
          // 第二次及以后的搜索，提供更详细的提示
          prompt = '请详细分析图片中的题目，提供更加深入的解题思路和方法，但仍然不要直接给出完整答案。请按照以下格式回复：\n## 题目分析\n[详细分析题目要求、关键信息和解题方向]\n\n## 详细解题思路\n1. [第一个思路的详细说明，包括相关知识点和应用方法]\n2. [第二个思路的详细说明，包括相关知识点和应用方法]\n3. [第三个思路的详细说明，包括相关知识点和应用方法]\n\n## 关键步骤提示\n[提供解题的关键步骤和方法，但不给出完整解答]\n\n## 相关知识点\n[列出与题目相关的知识点和概念，帮助理解]\n\n注意：请不要直接给出完整答案，而是提供更详细的解题指导和思路分析。'
        }

        // 如果有文本，添加到提示词中
        if (this.searchQuery.trim()) {
          prompt += `\n补充说明：${this.searchQuery}`
        }

        // 使用FormData直接上传图片文件，避免Base64数据过大
        this.loading = true
        this.aiResponse = ''
        this.noResults = false

        // 显示正在处理的提示
        this.$message({
          message: '正在处理图片，请稍候...',
          type: 'info',
          duration: 2000
        })

        uploadAndAnalyzeImage(this.imageFile, prompt)
          .then(this.handleApiResponse)
          .catch(this.handleApiError)
          .finally(() => {
            this.loading = false
          })
      } catch (error) {
        this.handleApiError(error)
        this.loading = false
      }
    },

    // 处理API响应的通用方法
    handleApiResponse(response) {
      console.log('AI响应数据:', response)

      // 根据后端代码，响应结构为Result类型，成功码为1
      if (response.code === 1) {
        // data字段包含AI的回答
        this.aiResponse = response.data
        this.noResults = !this.aiResponse

        // 如果成功获取到响应，加载KaTeX渲染数学公式
        if (this.aiResponse) {
          this.$nextTick(() => {
            this.renderMathFormulas()
          })
        }
      } else if (response.code === 200) {
        // 某些API可能使用200作为成功码
        this.aiResponse = response.data || response.result || response.message || ''
        this.noResults = !this.aiResponse

        if (this.aiResponse) {
          this.$nextTick(() => {
            this.renderMathFormulas()
          })
        }
      } else if (typeof response === 'string') {
        // 直接返回字符串的情况
        this.aiResponse = response
        this.noResults = false

        this.$nextTick(() => {
          this.renderMathFormulas()
        })
      } else {
        // 失败情况
        this.aiResponse = ''
        this.noResults = true
        this.$message.error(response.msg || '搜索失败')
      }
    },

    // 处理API错误的通用方法
    handleApiError(error) {
      console.error('AI 搜题失败:', error)
      this.aiResponse = ''
      this.noResults = true

      // 提取更友好的错误信息
      let errorMsg = '搜索失败'

      if (error.response && error.response.data) {
        errorMsg += ': ' + (error.response.data.msg || error.message || '未知错误')
      } else if (error.message && error.message.includes('400')) {
        errorMsg = '图片可能过大或格式不支持，请尝试使用更小的图片'
      } else if (error.message && error.message.includes('413')) {
        errorMsg = '图片太大，无法处理。请尝试裁剪或使用更小的图片'
      } else {
        errorMsg += ': ' + (error.message || '未知错误')
      }

      this.$message.error(errorMsg)
    },

    // 渲染数学公式
    renderMathFormulas() {
      if (window.katex) {
        try {
          const formulas = document.querySelectorAll('.inline-formula, .block-formula, .math-formula')

          // 第一步：预处理公式内容，统一格式
          formulas.forEach(el => {
            try {
              let formula = el.textContent
                .replace(/^\\\(|\\\)$/g, '')
                .replace(/^\\\[|\\\]$/g, '')
                .trim()

              // 使用全局公式修复函数
              if (window.fixLatexNotation) {
                formula = window.fixLatexNotation(formula)
              }

              // 使用KaTeX渲染公式
              window.katex.render(formula, el, {
                throwOnError: false,
                displayMode: el.classList.contains('block-formula') || el.classList.contains('math-formula'),
                output: 'html',
                trust: true,
                strict: false
              })
            } catch (error) {
              console.error('KaTeX渲染失败:', error, '公式内容:', el.textContent)
              // 保留原始内容，添加错误样式
              el.classList.add('render-error')
              el.setAttribute('title', '公式渲染失败: ' + error.message)
            }
          })

          // 处理渲染后的位置对齐
          document.querySelectorAll('.inline-formula .katex').forEach(el => {
            el.style.verticalAlign = 'middle'
          })
        } catch (e) {
          console.error('公式渲染过程出错:', e)
        }
      } else {
        console.warn('KaTeX未加载，正在尝试加载...')
        // 尝试动态加载KaTeX
        this.loadKaTeX()
      }
    },

    // 添加动态加载KaTeX的方法
    loadKaTeX() {
      if (window.katex) return // 已加载则直接返回

      const loadScript = (src) => {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script')
          script.src = src
          script.onload = resolve
          script.onerror = reject
          document.head.appendChild(script)
        })
      }

      const loadCSS = (href) => {
        return new Promise((resolve, reject) => {
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = href
          link.onload = resolve
          link.onerror = reject
          document.head.appendChild(link)
        })
      }

      Promise.all([
        loadCSS('https://unpkg.com/katex@0.16.4/dist/katex.min.css'),
        loadScript('https://unpkg.com/katex@0.16.4/dist/katex.min.js')
      ]).then(() => {
        // 加载扩展
        return Promise.all([
          loadScript('https://unpkg.com/katex@0.16.4/dist/contrib/auto-render.min.js'),
          loadScript('https://unpkg.com/katex@0.16.4/dist/contrib/mhchem.min.js')
        ])
      }).then(() => {
        console.log('KaTeX加载成功')
        // 重新尝试渲染公式
        setTimeout(() => {
          this.renderMathFormulas()
        }, 100)
      }).catch(error => {
        console.error('KaTeX加载失败:', error)
      })
    }
  }
}
</script>

<style scoped>
.search-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.search-header {
  text-align: center;
  margin-bottom: 30px;
}

.search-header h1 {
  font-size: 28px;
  color: #303133;
  margin-bottom: 10px;
}

.search-header p {
  color: #606266;
  font-size: 16px;
}

.search-box-card {
  margin-bottom: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
}

.search-box {
  padding: 10px;
}

.search-input {
  margin-bottom: 15px;
}

.search-input .el-textarea__inner {
  font-size: 15px;
  padding: 12px;
  border-radius: 6px;
}

.upload-preview {
  margin-bottom: 20px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  padding: 10px;
  background-color: #f8f8f8;
}

.preview-wrapper {
  position: relative;
  display: inline-block;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-actions {
  position: absolute;
  top: 5px;
  right: 5px;
}

.search-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 5px;
}

.image-upload {
  display: inline-block;
}

.search-results {
  margin-top: 30px;
}

.search-results h2 {
  font-size: 20px;
  margin-bottom: 15px;
  color: #303133;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
}

.result-card {
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.ai-response {
  display: flex;
  padding: 20px;
}

.ai-avatar {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  margin-right: 20px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #ecf5ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-avatar i {
  font-size: 24px;
  color: #409EFF;
}

.ai-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ai-content {
  flex-grow: 1;
  line-height: 1.7;
  font-size: 15px;
  color: #333;
}

.ai-content p {
  margin-bottom: 15px;
}

.ai-content ul,
.ai-content ol {
  padding-left: 20px;
  margin-bottom: 15px;
}

.ai-content h1,
.ai-content h2,
.ai-content h3,
.ai-content h4 {
  margin-top: 20px;
  margin-bottom: 12px;
  color: #1a1a1a;
}

.empty-card {
  text-align: center;
  padding: 40px 0;
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
}

.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-icon {
  font-size: 48px;
  color: #909399;
  margin-bottom: 15px;
}

.empty-text {
  color: #909399;
  font-size: 16px;
}

/* 数学公式样式 */
.inline-formula {
  display: inline-block;
  margin: 0 2px;
  vertical-align: middle;
}

.block-formula {
  display: block;
  margin: 15px 0;
  text-align: center;
  overflow-x: auto;
  padding: 10px 0;
}

.math-formula {
  display: block;
  margin: 15px 0;
  text-align: center;
  overflow-x: auto;
  padding: 15px 0;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.render-error {
  color: #f56c6c;
  border: 1px dashed #f56c6c;
  padding: 5px;
  margin: 5px 0;
  display: inline-block;
}

/* 确保公式在移动设备上也能正常显示 */
@media screen and (max-width: 768px) {
  .block-formula, .math-formula {
    font-size: 90%;
    padding: 10px 5px;
    overflow-x: auto;
  }
}
</style>

<style>
/* 全局样式 */
.katex {
  font-size: 1.1em !important;
  line-height: 1.5em !important;
}

.katex-display {
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px 0;
  margin: 10px 0;
  text-align: center;
}

/* 改进公式容器样式 */
.math-formula, .block-formula {
  background-color: #f9f9f9;
  border-radius: 6px;
  padding: 10px;
  margin: 15px 0;
  overflow-x: auto;
}

.inline-formula {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin: 0 3px;
}

/* 针对MacOS和iOS设备的优化 */
@media screen and (-webkit-min-device-pixel-ratio: 0) {
  .katex-display > .katex {
    min-width: min-content;
  }
}

/* 优化分数显示 */
.katex .mfrac .frac-line {
  border-bottom-width: 1px;
  margin: 0 -0.1em;
}

.katex .mfrac .frac-line::after {
  border-bottom-width: 1px;
}

/* 分子分母的间距 */
.katex .mfrac .msupsub .mfrac-numer {
  padding-bottom: 0.1em;
}

.katex .mfrac .msupsub .mfrac-denom {
  padding-top: 0.1em;
}

/* 优化上标下标显示 */
.katex .msupsub {
  text-align: left;
}

/* 改进大型操作符 (如求和、积分) 的显示 */
.katex .mop {
  margin-top: 0.1em;
  margin-bottom: 0.15em;
}

/* 优化矩阵显示 */
.katex .array {
  margin: 0 0.1em;
}

/* 提高公式渲染错误的可见性 */
.render-error {
  color: #f56c6c;
  border: 1px dashed #f56c6c;
  padding: 8px;
  background-color: #fef0f0;
  border-radius: 4px;
  margin: 5px 0;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
